import {useLocation} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useMemo} from 'react'

import {
  LAYOUT_PARAM,
  type Layout,
  readSavedViewUrlParams,
  type SavedViewUrlParams,
} from '../../utils/saved-view-url-params'

interface SavedViewOptions extends SavedViewUrlParams {
  setLayout: (layout: Layout) => void
}

/**
 * Reads and writes the configuration in the URL.
 *
 * The writable subset is only what the fetch respects today: `layout`. `groupBy` is
 * readable so future consumers (e.g. a URL-driven Group by control) can pick it up, but no
 * setter is exposed until the board mapper honours it. See `create-repository-board-adapter.ts`.
 *
 * Card attribute visibility (`VisibleFieldsProvider`) and the view's field schema
 * (`ViewDefinitionProvider`) are not managed here — neither lives in the URL.
 */
export function useSavedViewOptions(): SavedViewOptions {
  const {pathname, search} = useLocation()
  const navigate = useNavigate()
  const params = useMemo(() => readSavedViewUrlParams(new URLSearchParams(search)), [search])

  const commit = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(search)
      mutate(next)
      next.delete('page')
      const query = next.toString()
      navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true})
    },
    [navigate, pathname, search],
  )

  const setLayout = useCallback(
    (nextLayout: Layout) => {
      if (nextLayout === params.layout) return
      commit(next => {
        if (nextLayout === 'list') next.delete(LAYOUT_PARAM)
        else next.set(LAYOUT_PARAM, nextLayout)
      })
    },
    [commit, params.layout],
  )

  return {...params, setLayout}
}
